import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LanguageService } from '../../services/language.service';
import { FlatTranslation } from '../../types/language';
import { MaterialHeaderComponent } from '../shared/material-header/material-header.component';
import { MaterialTableComponent } from '../shared/material-table/material-table.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { SortableHeader } from '../../types/table';

@Component({
  selector: 'app-language-settings',
  standalone: true,
  imports: [FormsModule, MaterialHeaderComponent, MaterialTableComponent, TranslatePipe],
  templateUrl: './language-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSettingsComponent {
  private languageService = inject(LanguageService);

  searchTerm = signal('');
  isModalOpen = signal(false);
  editItem = signal<FlatTranslation | null>(null);
  isNew = signal(false);

  private allTranslations = signal<FlatTranslation[]>([]);

  headers = computed<SortableHeader[]>(() => {
    // This makes the headers reactive to language changes
    this.languageService.currentLanguage(); 
    return [
      { key: 'key', label: this.languageService.t('language.key'), sortable: true },
      { key: 'en', label: this.languageService.t('language.enValue'), sortable: true },
      { key: 'ko', label: this.languageService.t('language.krValue'), sortable: true },
      { key: 'actions', label: this.languageService.t('common.actions'), sortable: false },
    ];
  });

  constructor() {
    this.allTranslations.set(this.languageService.getFlatTranslations());
  }

  filteredTranslations = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const all = this.allTranslations();
    if (!term) {
      return all;
    }
    return all.filter(
      (item) =>
        item.key.toLowerCase().includes(term) ||
        item.en.toLowerCase().includes(term) ||
        item.ko.toLowerCase().includes(term)
    );
  });

  openAddModal() {
    this.editItem.set({ key: '', en: '', ko: '' });
    this.isNew.set(true);
    this.isModalOpen.set(true);
  }

  openEditModal(item: FlatTranslation) {
    this.editItem.set({ ...item });
    this.isNew.set(false);
    this.isModalOpen.set(true);
  }

  closeModal() {
    this.isModalOpen.set(false);
    this.editItem.set(null);
  }

  saveTranslation() {
    const item = this.editItem();
    if (item && item.key && item.en) {
      this.languageService.updateTranslation(item.key, item.en, item.ko);
      this.allTranslations.set(this.languageService.getFlatTranslations());
      this.closeModal();
    }
  }
}
